/**
 * Session Expiry Provider
 *
 * Provides session expiry detection and the SessionExpiredModal at the app root level.
 * Components can use `useSessionExpiryContext` to access `markIntentionalLogout`
 * which should be called before signing out to prevent the modal from showing.
 *
 * Also runs the session expiry warning hook which shows a proactive toast
 * 5 minutes before session expires, giving users a chance to extend their session.
 */

import { createContext, useContext, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useSessionExpiry } from '@/hooks/useSessionExpiry';
import { useSessionExpiryWarning } from '@/hooks/useSessionExpiryWarning';
import { SessionExpiredModal } from '@/components/auth/SessionExpiredModal';

interface SessionExpiryContextValue {
  /**
   * Call this before signing out to mark it as intentional.
   * This prevents the session expired modal from showing.
   */
  markIntentionalLogout: () => void;
}

const SessionExpiryContext = createContext<SessionExpiryContextValue | null>(null);

// Pages that don't require authentication to view
const PUBLIC_PATH_PREFIXES = [
  '/',
  '/token/',
  '/tokens/',
  '/creator/',
  '/leaderboards',
  '/how-it-works',
  '/verified',
  '/user-not-found',
  '/auth/',
];

/**
 * Determines if a pathname is a public page that doesn't require auth
 */
function isPublicPage(pathname: string): boolean {
  // Exact match for homepage
  if (pathname === '/') return true;

  // Prefix matches for other public routes
  return PUBLIC_PATH_PREFIXES.some(
    (prefix) => prefix !== '/' && pathname.startsWith(prefix)
  );
}

interface SessionExpiryProviderProps {
  children: ReactNode;
}

export function SessionExpiryProvider({ children }: SessionExpiryProviderProps) {
  const { showExpiredModal, setShowExpiredModal, markIntentionalLogout } =
    useSessionExpiry();
  const location = useLocation();

  // Run the warning hook - it manages its own toast notifications
  // This shows a proactive warning 5 minutes before session expires
  useSessionExpiryWarning();

  // Don't show modal if we're already on an auth page
  const shouldShowModal = showExpiredModal && !location.pathname.startsWith('/auth');

  return (
    <SessionExpiryContext.Provider value={{ markIntentionalLogout }}>
      {children}
      <SessionExpiredModal
        open={shouldShowModal}
        onOpenChange={setShowExpiredModal}
        isPublicPage={isPublicPage(location.pathname)}
      />
    </SessionExpiryContext.Provider>
  );
}

/**
 * Hook to access session expiry context
 * Must be used within SessionExpiryProvider
 */
export function useSessionExpiryContext(): SessionExpiryContextValue {
  const context = useContext(SessionExpiryContext);

  if (!context) {
    throw new Error(
      'useSessionExpiryContext must be used within SessionExpiryProvider'
    );
  }

  return context;
}

/**
 * Safe hook that returns null if not within provider
 * Useful for components that may be rendered outside the provider
 */
export function useSessionExpiryContextSafe(): SessionExpiryContextValue | null {
  return useContext(SessionExpiryContext);
}

export default SessionExpiryProvider;
